import { jsPDF } from 'jspdf';
import { formatMoney, formatMonthly, formatQuoteDate, formatStatus } from './quotes';
import { CARE_TIER_LABELS, PAYMENT_NOTES } from '../constants/pricing';
import { BUSINESS, businessAddressLine } from '../constants/business';

const MARGIN = 20;
const PAGE_WIDTH = 210; // A4 portrait, mm
const PAGE_HEIGHT = 297;
const CONTENT_WIDTH = PAGE_WIDTH - MARGIN * 2;
const BOTTOM_LIMIT = PAGE_HEIGHT - MARGIN;

function slugify(text) {
  return (text || 'quote')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 50) || 'quote';
}

// Renders the quote exactly as it stands in Firestore — line items, contract
// wording, and the signature — into a self-contained PDF the client can keep.
// Everything here comes from the stored document, so a downloaded copy is a
// faithful record of what was agreed rather than a re-render of a live page.
export function downloadSignedQuote(quote) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  let y = MARGIN;

  const newPageIfNeeded = (needed) => {
    if (y + needed > BOTTOM_LIMIT) {
      doc.addPage();
      y = MARGIN;
    }
  };

  const write = (text, { size = 10, style = 'normal', gap = 5, color = '#000000' } = {}) => {
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(color);
    const lines = doc.splitTextToSize(String(text), CONTENT_WIDTH);
    lines.forEach((line) => {
      newPageIfNeeded(gap);
      doc.text(line, MARGIN, y);
      y += gap;
    });
  };

  const rule = () => {
    newPageIfNeeded(6);
    doc.setDrawColor(200);
    doc.line(MARGIN, y, PAGE_WIDTH - MARGIN, y);
    y += 6;
  };

  // Header
  write(BUSINESS.name, { size: 16, style: 'bold', gap: 7 });
  write(businessAddressLine(), { size: 8, color: '#666666', gap: 4 });
  write(`Company no. ${BUSINESS.companyNumber}`, { size: 8, color: '#666666', gap: 4 });
  y += 5;

  write(quote.title, { size: 12, style: 'bold', gap: 7 });
  write(
    quote.clientName ? `Prepared for ${quote.clientName} (${quote.clientEmail})` : `Prepared for ${quote.clientEmail}`,
    { size: 9, color: '#666666' },
  );
  if (formatQuoteDate(quote.createdAt)) {
    write(`Issued ${formatQuoteDate(quote.createdAt)}`, { size: 9, color: '#666666' });
  }
  // Only meaningful while it's still an open offer — on a signed copy the
  // acceptance date is what matters.
  if (quote.status === 'sent' && formatQuoteDate(quote.expiresAt)) {
    write(`Valid until ${formatQuoteDate(quote.expiresAt)}`, { size: 9, color: '#666666' });
  }
  y += 3;
  rule();

  // Line items, each with the amount right-aligned on the same baseline.
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  write('Quote', { size: 10, style: 'bold', gap: 7 });

  (quote.lineItems || []).forEach((item) => {
    newPageIfNeeded(6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.setTextColor('#000000');
    const amount = formatMoney(item.amount);
    const amountWidth = doc.getTextWidth(amount);
    const descriptionLines = doc.splitTextToSize(item.description, CONTENT_WIDTH - amountWidth - 6);
    doc.text(descriptionLines, MARGIN, y);
    doc.text(amount, PAGE_WIDTH - MARGIN - amountWidth, y);
    y += descriptionLines.length * 5 + 1;

    // Indented under their parent line, with no amount — the price above
    // covers them.
    (item.features || []).forEach((feature) => {
      doc.setFontSize(8.5);
      doc.setTextColor('#666666');
      doc.splitTextToSize(`–  ${feature}`, CONTENT_WIDTH - 20).forEach((line) => {
        newPageIfNeeded(4.5);
        doc.text(line, MARGIN + 6, y);
        y += 4.5;
      });
    });
    if ((item.features || []).length) y += 2;
  });

  y += 2;
  rule();

  if (quote.discountPercent > 0) {
    const money = (label, value, color) => {
      newPageIfNeeded(6);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor(color);
      const w = doc.getTextWidth(value);
      doc.text(label, MARGIN, y);
      doc.text(value, PAGE_WIDTH - MARGIN - w, y);
      y += 5.5;
    };
    money('Subtotal', formatMoney(quote.subtotal), '#666666');
    money(`Discount ${quote.discountPercent}%`, `-${formatMoney(quote.discountAmount)}`, '#1a7f37');
    y += 1;
  }

  newPageIfNeeded(8);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.setTextColor('#000000');
  const total = formatMoney(quote.total);
  doc.text('Total', MARGIN, y);
  doc.text(total, PAGE_WIDTH - MARGIN - doc.getTextWidth(total), y);
  y += 10;

  // Guarded: quotes created before payment stages existed have neither field.
  if (quote.paymentSchedule?.length) {
    rule();
    write('How it\'s paid', { size: 10, style: 'bold', gap: 6 });
    quote.paymentSchedule.forEach((stage) => {
      newPageIfNeeded(6);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.setTextColor('#333333');
      const right = `${stage.percent}%  ${formatMoney(stage.amount)}`;
      const rightWidth = doc.getTextWidth(right);
      doc.text(stage.label, MARGIN, y);
      doc.text(right, PAGE_WIDTH - MARGIN - rightWidth, y);
      y += 5;
    });
    y += 2;
    PAYMENT_NOTES.forEach((note) => {
      write(note, { size: 7.5, color: '#888888', gap: 4 });
      y += 1;
    });
    y += 2;
  }

  if (quote.carePlan) {
    rule();
    write('Care plan', { size: 10, style: 'bold', gap: 6 });
    newPageIfNeeded(6);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.setTextColor('#333333');
    const care = formatMonthly(quote.carePlan.monthlyAmount);
    const careWidth = doc.getTextWidth(care);
    doc.text(CARE_TIER_LABELS[quote.carePlan.tier] || quote.carePlan.tier, MARGIN, y);
    doc.text(care, PAGE_WIDTH - MARGIN - careWidth, y);
    y += 7;
    const revertsAfter = quote.carePlan.listAmount > quote.carePlan.monthlyAmount
      && quote.carePlan.discountMonths > 0
      ? ` The reduced rate covers the first ${quote.carePlan.discountMonths} month`
        + `${quote.carePlan.discountMonths === 1 ? '' : 's'}, after which it is `
        + `${formatMonthly(quote.carePlan.listAmount)} a month.`
      : '';
    write(
      `Billed monthly from launch, separate from the total above.${revertsAfter}`,
      { size: 7.5, color: '#888888', gap: 4 },
    );
    y += 2;
  }

  if (quote.notes) {
    rule();
    write('Notes', { size: 10, style: 'bold', gap: 6 });
    write(quote.notes, { size: 9, color: '#333333' });
    y += 4;
  }

  if (quote.contractText) {
    rule();
    write('Contract', { size: 10, style: 'bold', gap: 6 });
    // Blank lines are preserved so the admin's paragraph breaks survive.
    quote.contractText.split('\n').forEach((paragraph) => {
      if (paragraph.trim() === '') {
        y += 3;
      } else {
        write(paragraph, { size: 9, color: '#333333', gap: 4.5 });
      }
    });
    y += 4;
  }

  if (quote.contractUrl) {
    write(`Original document: ${quote.contractUrl}`, { size: 8, color: '#666666' });
    y += 2;
  }

  // Signature block
  rule();
  write('Signature', { size: 10, style: 'bold', gap: 6 });
  if (quote.status === 'accepted') {
    write(`Accepted and signed by: ${quote.signedName || quote.clientEmail}`, { size: 10 });
    // Who they signed on behalf of. Skipped when it just repeats the signer,
    // as it would for a sole trader whose name is the business name.
    if (quote.clientName && quote.clientName.trim() !== (quote.signedName || '').trim()) {
      write(`For: ${quote.clientName}`, { size: 9, color: '#333333' });
    }
    write(`Account: ${quote.clientEmail}`, { size: 9, color: '#333333' });
    if (formatQuoteDate(quote.respondedAt)) {
      write(`Date: ${formatQuoteDate(quote.respondedAt)}`, { size: 9, color: '#333333' });
    }
    y += 2;
    write(
      'Signed electronically by typed name, confirmed against the signed-in account above. '
      + 'The wording shown here is the wording stored with the quote at the time of signing.',
      { size: 7.5, color: '#888888', gap: 4 },
    );
  } else {
    write(`Status: ${formatStatus(quote.status)}`, { size: 10 });
  }

  doc.save(`${slugify(quote.title)}-${quote.status}.pdf`);
}
